import React from 'react';
import { BookOpen, GraduationCap, Target, TrendingUp, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function EducationFocused() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <Link to="/about" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to About
        </Link>

        <div className="mb-12">
          <div className="inline-flex items-center gap-3 bg-primary/10 border border-primary/30 rounded-full px-6 py-3 mb-6">
            <BookOpen className="w-6 h-6 text-primary" />
            <span className="text-primary font-semibold">Our Approach</span>
          </div>
          <h1 className="text-5xl font-heading font-bold mb-6">Education Focused</h1>
          <p className="text-xl text-muted-foreground">
            Every livestream, lesson, and discussion is designed to help beginners understand and enjoy Texas Hold'em.
          </p>
        </div>

        <div className="prose prose-invert max-w-none">
          <div className="bg-card border border-border rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-3">
              <GraduationCap className="w-6 h-6 text-primary" />
              Teaching Philosophy
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              At Luck or Skills, education isn't an afterthought—it's our core mission. We've designed every aspect 
              of our platform with one goal in mind: helping you understand and master Texas Hold'em poker.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Unlike other poker resources that assume knowledge or use confusing jargon, we break down every 
              concept into clear, understandable lessons that build on each other logically.
            </p>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-3">
              <Target className="w-6 h-6 text-primary" />
              Our Educational Approach
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-primary">Structured Learning Path</h3>
                <p className="text-muted-foreground">
                  Our lessons follow a carefully designed curriculum that takes you from absolute beginner to 
                  confident player. Each lesson builds on the previous one, ensuring you develop a solid foundation.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-primary">Live Interactive Sessions</h3>
                <p className="text-muted-foreground">
                  Every week, our expert panel hosts live discussions where we analyze real hands, answer questions 
                  in real-time, and demonstrate concepts with live examples. No pre-recorded lectures here.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-primary">Practice Makes Perfect</h3>
                <p className="text-muted-foreground">
                  Theory is important, but application is crucial. Our practice hand feature lets you test your 
                  knowledge in realistic scenarios with instant feedback from our AI coach.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-primary">Personalized Guidance</h3>
                <p className="text-muted-foreground">
                  Submit your hands for review, ask questions during live sessions, and get tailored advice from 
                  experienced players who understand your learning journey.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-3">
              <TrendingUp className="w-6 h-6 text-primary" />
              What You'll Learn
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-primary mb-2">Fundamentals</h3>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  <li>• Hand rankings and rules</li>
                  <li>• Betting structures</li>
                  <li>• Position and its importance</li>
                  <li>• Starting hand selection</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-primary mb-2">Strategy</h3>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  <li>• Pre-flop strategy</li>
                  <li>• Post-flop play</li>
                  <li>• Bet sizing</li>
                  <li>• Reading opponents</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-primary mb-2">Mathematics</h3>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  <li>• Pot odds</li>
                  <li>• Implied odds</li>
                  <li>• Expected value</li>
                  <li>• Probability basics</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-primary mb-2">Psychology</h3>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  <li>• Tilt management</li>
                  <li>• Bankroll management</li>
                  <li>• Table image</li>
                  <li>• Mental game</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-primary/10 border border-primary/30 rounded-2xl p-8">
            <h3 className="text-xl font-heading font-bold mb-3">Our Guarantee</h3>
            <p className="text-muted-foreground">
              If you follow our structured learning path, participate in live sessions, and practice consistently, 
              you'll see measurable improvement in your poker game within 30 days. We're committed to your success.
            </p>
          </div>

          <div className="mt-12 text-center">
            <Link 
              to="/lessons" 
              className="inline-flex items-center gap-3 bg-primary hover:bg-primary/90 text-black px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105"
            >
              Start Learning
              <BookOpen className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}